import { GameStatus, KeyboardStatus, ValidatedGuess, GuessHistory, PreviousWord } from "@/lib/types/GameTypes";
import { saveGameState } from "../GamePersistenceV2";
import { handleGameWinFinal } from "./GameStatusManager";
import { timeoutManager } from "@/lib/managers/TimeoutManager";

export const handleGameProgression = async (
  currentWord: string,
  currentLevel: number,
  guesses: ValidatedGuess[],
  guessHistory: GuessHistory,
  levelAttempts: number[],
  gameStatus: GameStatus,
  keyboardStatus: KeyboardStatus,
  validatedGuess: ValidatedGuess,
  onCorrectGuess: () => void,
  setIsTransitioning: (value: boolean) => void,
  setGuesses: (guesses: ValidatedGuess[]) => void,
  setKeyboardStatus: (status: KeyboardStatus) => void,
  setCurrentGuess: (guess: string) => void,
  setGameStatus: (status: GameStatus) => void,
  setShowGameCompletion: (show: boolean) => void,
  setPreviousWord: (word: PreviousWord) => void
) => {
  console.log('🏆 GAME PROGRESSION START:', {
    currentLevel,
    gameStatus,
    isFinalLevel: currentLevel >= 4,
    currentWord,
    guessCount: guesses.length,
    timestamp: new Date().toISOString()
  });

  // Clear any existing progression timeouts to prevent race conditions
  timeoutManager.clearTimeout(`progression-${currentLevel}`);
  timeoutManager.clearTimeout(`transition-${currentLevel}`);

  // ATOMIC BLOCK 1: Prepare all state updates without making changes
  const newLevelAttempts = [...levelAttempts];
  
  // Only increment level attempts if not already set
  if (!newLevelAttempts[currentLevel - 1] || newLevelAttempts[currentLevel - 1] < guesses.length) {
    newLevelAttempts[currentLevel - 1] = guesses.length;
    console.log('🏆 Updated level attempts:', { 
      level: currentLevel, 
      attempts: guesses.length,
      newLevelAttempts
    });
  }

  const updatedGuessHistory = [...guessHistory];
  // Key by BOTH level AND word to prevent cross-word contamination
  const existingLevelHistoryIndex = updatedGuessHistory.findIndex(h => 
    h.level === currentLevel && h.word === currentWord
  );
  
  if (existingLevelHistoryIndex !== -1) {
    // Compare existing guesses with new ones to prevent duplicates
    if (updatedGuessHistory[existingLevelHistoryIndex].guesses.length !== guesses.length) {
      updatedGuessHistory[existingLevelHistoryIndex].guesses = [...guesses];
      console.log('🏆 Updated existing level history:', { 
        level: currentLevel,
        word: currentWord,
        guessesCount: guesses.length 
      });
    }
  } else {
    // Create new history entry for this level and word
    updatedGuessHistory.push({
      level: currentLevel,
      word: currentWord,
      guesses: [...guesses]
    });
    console.log('🏆 Added new level history:', { 
      level: currentLevel,
      word: currentWord,
      guessesCount: guesses.length 
    });
  }
  
  // Check if this is the final level
  const isFinalLevel = currentLevel >= 4;
  const nextGameStatus = isFinalLevel ? 'won' : gameStatus;

  try {
    // ATOMIC BLOCK 2: Signal transition start
    console.log('🏆 GAME PROGRESSION - Setting UI transition state', {
      timestamp: new Date().toISOString()
    });
    setIsTransitioning(true);
    
    // ATOMIC BLOCK 3: Update UI state immediately for better UX
    setGuesses([]);
    setKeyboardStatus({});
    setCurrentGuess("");
    
    // ATOMIC BLOCK 4: Save state with transition flag
    console.log('🏆 GAME PROGRESSION - Saving initial transition state', {
      isTransitioning: true,
      gameStatus: nextGameStatus,
      timestamp: new Date().toISOString()
    });
    
    await saveGameState(
      updatedGuessHistory,
      newLevelAttempts,
      nextGameStatus,
      currentWord,
      currentLevel,
      guesses,
      guesses.length,
      keyboardStatus,
      { word: currentWord, guess: validatedGuess }, // Include previousWord right away
      true // isTransitioning flag is on
    );

    // ATOMIC BLOCK 5: Update UI for previous word
    setPreviousWord({
      word: currentWord,
      guess: validatedGuess
    });
    
    // ATOMIC BLOCK 6: Handle game win or level transition
    if (isFinalLevel) {
      console.log('🏆 GAME PROGRESSION - Final level complete, triggering game win', {
        timestamp: new Date().toISOString()
      });
      // Handle game win if final level - this updates streak
      try {
        await handleGameWinFinal(setGameStatus, setShowGameCompletion, currentLevel, true);
        console.log('🏆 GAME PROGRESSION - handleGameWin completed successfully');
      } catch (winError) {
        console.error('🏆 GAME PROGRESSION - handleGameWin failed:', winError);
        throw winError;
      }
    } else {
      // Otherwise trigger normal level transition
      console.log('🏆 GAME PROGRESSION - Triggering level transition', {
        timestamp: new Date().toISOString()
      });
      onCorrectGuess();
    }
    
    // ATOMIC BLOCK 7: Short delay for animation using timeout manager
    await new Promise<void>(resolve => {
      timeoutManager.setTimeout(`progression-${currentLevel}`, () => resolve(), 200);
    });
    
    // ATOMIC BLOCK 8: Save final state
    console.log('🏆 GAME PROGRESSION - Saving final state', {
      isTransitioning: false,
      newLevel: isFinalLevel ? currentLevel : currentLevel + 1,
      timestamp: new Date().toISOString()
    });
    
    if (!isFinalLevel) {
      await saveGameState(
        updatedGuessHistory,
        newLevelAttempts,
        nextGameStatus,
        currentWord,
        currentLevel + 1, // Increment level only if not final
        [],
        0,
        {},
        { word: currentWord, guess: validatedGuess },
        false // isTransitioning is now false
      );
    }
    
    // ATOMIC BLOCK 9: Turn off transition flag using timeout manager
    timeoutManager.setTimeout(`transition-${currentLevel}`, () => {
      console.log('🏆 GAME PROGRESSION - Turning off transition flag', {
        timestamp: new Date().toISOString()
      });
      setIsTransitioning(false);
    }, 50);
    
  } catch (error) {
    console.error('🏆 GAME PROGRESSION ERROR:', error);
    
    // ATOMIC BLOCK 10: Error recovery
    setIsTransitioning(false);
    
    // Clean up any lingering timeouts
    timeoutManager.clearTimeout(`progression-${currentLevel}`);
    timeoutManager.clearTimeout(`transition-${currentLevel}`);
    
    // Try to save a rollback state if there was an error
    try {
      await saveGameState(
        guessHistory, // Original history
        levelAttempts, // Original attempts
        gameStatus, // Original status
        currentWord,
        currentLevel, // Original level
        guesses, // Original guesses
        guesses.length,
        keyboardStatus, // Original keyboard
        null, // No previous word
        false // Not transitioning
      );
      console.error('🏆 GAME PROGRESSION - Recovered to original state after error');
    } catch (recoveryError) {
      console.error('🏆 GAME PROGRESSION - Failed to recover state:', recoveryError);
    }
    
    // Re-throw for caller to handle
    throw error;
  }

  console.log('🏆 GAME PROGRESSION COMPLETE', {
    finalLevel: isFinalLevel,
    newLevel: isFinalLevel ? currentLevel : currentLevel + 1,
    timestamp: new Date().toISOString()
  });

  return {
    newLevelAttempts,
    updatedGuessHistory
  };
};
